import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { CorrectionSummaryRow, FormReadCorrection, FormReadCorrectionReason } from "./corrections";

export const QUERY_KEY = "form-read-corrections";
const ANSWER_MUTATION_KEY = [QUERY_KEY, "answer"];
const UNANSWERED = "judge_was_right.is.null,and(reason.neq.abstained,replacement_was_right.is.null)";

const LIST_PAGE_SIZE = 1000;

export type CorrectionAnswer = { judge_was_right: boolean | null } | { replacement_was_right: boolean | null };

export interface CorrectionListQuery {
  includeAnswered: boolean;
  reasons: readonly FormReadCorrectionReason[];
  lowConfidenceOnly: boolean;
}

interface CorrectionList {
  rows: CorrectionSummaryRow[];
  total: number;
}

type PageCursor = Pick<CorrectionSummaryRow, "created_at" | "id">;

/** Pages follow the sort order from the last row seen, because the queue shrinks as other admins answer rows. */
async function fetchCorrectionPage(
  { includeAnswered, reasons, lowConfidenceOnly }: CorrectionListQuery,
  cursor: PageCursor | null,
): Promise<CorrectionList> {
  let query = supabase
    .from("form_read_corrections")
    .select("id, encounter_id, reason, low_confidence, created_at", cursor ? undefined : { count: "exact" });
  if (!includeAnswered) query = query.or(UNANSWERED);
  if (reasons.length > 0) query = query.in("reason", [...reasons]);
  if (lowConfidenceOnly) query = query.eq("low_confidence", true);
  if (cursor) {
    query = query.or(`created_at.lt.${cursor.created_at},and(created_at.eq.${cursor.created_at},id.gt.${cursor.id})`);
  }

  const { data, count, error } = await query
    .order("created_at", { ascending: false })
    .order("id")
    .limit(LIST_PAGE_SIZE);
  if (error) throw error;
  const rows = data ?? [];
  return { rows, total: count ?? rows.length };
}

async function fetchEncounterCorrections(encounterId: string): Promise<FormReadCorrection[]> {
  const { data, error } = await supabase.from("form_read_corrections").select("*").eq("encounter_id", encounterId);
  if (error) throw error;
  return data ?? [];
}

export function useFormReadCorrectionList(listQuery: CorrectionListQuery) {
  const query = useInfiniteQuery({
    queryKey: [QUERY_KEY, "list", listQuery],
    queryFn: ({ pageParam }) => fetchCorrectionPage(listQuery, pageParam),
    initialPageParam: null as PageCursor | null,
    getNextPageParam: (lastPage): PageCursor | undefined =>
      lastPage.rows.length < LIST_PAGE_SIZE ? undefined : lastPage.rows[lastPage.rows.length - 1],
    placeholderData: keepPreviousData,
  });
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.rows) ?? [], [query.data]);

  return { ...query, rows, total: query.data?.pages[0]?.total ?? 0 };
}

export function useNextUnansweredEncounterId(currentEncounterId: string) {
  return useQuery({
    queryKey: [QUERY_KEY, "next", currentEncounterId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("form_read_corrections")
        .select("encounter_id")
        .or(UNANSWERED)
        .neq("encounter_id", currentEncounterId)
        .order("created_at", { ascending: false })
        .order("id")
        .limit(1);
      if (error) throw error;
      return data?.[0]?.encounter_id ?? null;
    },
    enabled: !!currentEncounterId,
  });
}

export function useEncounterFormReadCorrections(encounterId: string) {
  return useQuery({
    queryKey: [QUERY_KEY, "encounter", encounterId],
    queryFn: () => fetchEncounterCorrections(encounterId),
    enabled: !!encounterId,
  });
}

export function usePendingAnswerIds(): ReadonlySet<string> {
  const ids = useMutationState({
    filters: { mutationKey: ANSWER_MUTATION_KEY, status: "pending" },
    select: (mutation) => (mutation.state.variables as { row: FormReadCorrection }).row.id,
  });
  return new Set(ids);
}

export function useAnswerCorrection(encounterId: string) {
  const queryClient = useQueryClient();
  const encounterKey = [QUERY_KEY, "encounter", encounterId];
  const replaceRow = (saved: FormReadCorrection) =>
    queryClient.setQueryData<FormReadCorrection[]>(encounterKey, (rows) =>
      rows?.map((row) => (row.id === saved.id ? saved : row)),
    );

  return useMutation({
    mutationKey: ANSWER_MUTATION_KEY,
    mutationFn: async ({ row, answer }: { row: FormReadCorrection; answer: CorrectionAnswer }) => {
      const { data, error } = await supabase
        .from("form_read_corrections")
        .update(answer)
        .eq("id", row.id)
        .eq("updated_at", row.updated_at)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error("This row changed since you opened it. Reload the page to see it.");
      return data;
    },
    onMutate: async ({ row, answer }) => {
      await queryClient.cancelQueries({ queryKey: encounterKey });
      replaceRow({ ...row, ...answer });
    },
    onError: (_error, { row }) => replaceRow(row),
    onSuccess: replaceRow,
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY, "list"] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY, "next"] }),
      ]),
  });
}
